import React, { useCallback, useMemo } from "react";
import "react-quill/dist/quill.snow.css";
import Quill from "quill";

interface ImageTextSection {
    id: number;
    imageFile?: File | null;
    bodyCopy?: string;
    imagePath?: string;
}

interface TextImageProps {
    section: ImageTextSection;
    onChange: (id: number, updatedData: Partial<ImageTextSection>) => void;
    onClose: () => void;
    baseUrl?: string;
}

const TextImage: React.FC<TextImageProps> = ({ section, onChange, onClose, baseUrl }) => {
    const ReactQuill = require("react-quill");

    // Handle file changes
    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(section.id, { imageFile: e.target.files?.[0] || null });
        },
        [section.id, onChange]
    );

    // Handle body content changes
    const handleBodyChange = useCallback(
        (value: string) => {
            onChange(section.id, { bodyCopy: value });
        },
        [section.id, onChange]
    );

    // Preserve saved HTML directly — no normalization, same as AddCustomBodyComponent
    const cleanBody = useMemo(() => {
        if (!section.bodyCopy?.trim()) return "";
        return section.bodyCopy;
    }, [section.bodyCopy]);

    const getImageSrc = () => {
        if (section.imageFile) return URL.createObjectURL(section.imageFile);
        if (section.imagePath) return baseUrl ? `${baseUrl}${section.imagePath}` : section.imagePath;
        return undefined;
    };

    // Same modules as AddCustomBodyComponent including keyboard enter binding
    const modules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
        ],
        keyboard: {
            bindings: {
                customEnter: {
                    key: 13,
                    handler: function (this: any, range: any) {
                        const quill: Quill = this.quill;
                        const [line, offset] = quill.getLine(range.index);
                        const textBefore = line.domNode.textContent.slice(0, offset).trim();
                        const textAfter = line.domNode.textContent.slice(offset).trim();

                        // Double line break only on empty line
                        if (!textBefore && !textAfter) {
                            quill.insertText(range.index, "\n\n");
                        } else {
                            quill.insertText(range.index, "\n");
                        }

                        quill.setSelection(range.index + 1, "silent");
                        return false;
                    },
                },
            },
        },
    }), []);

    return (
        <div
            style={{
                padding: "10px",
                marginBottom: "20px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "fit-content"
            }}
        >
            <button
                style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    border: "none",
                    background: "none",
                    color: "black",
                    cursor: "pointer",
                }}
                onClick={onClose}
            >
                X
            </button>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", width: "100%" }}>
                {/* TEXT */}
                <div style={{ flex: "1", display: "flex", flexDirection: "column", marginTop: "10px", height: "fit-content" }}>
                    <label>Text Content</label>
                    <ReactQuill
                        value={cleanBody}
                        onChange={handleBodyChange}
                        modules={modules}
                        theme="snow"
                        style={{ minHeight: "150px", width: "100%" }}
                        placeholder="Enter text here..."
                    />
                </div>

                {/* IMAGE */}
                <div style={{ flex: "1", display: "flex", flexDirection: "column" }}>
                    <label>Image (Required)</label>
                    <input type="file" className="form-control" onChange={handleFileChange} />
                    {getImageSrc() && (
                        <img
                            src={getImageSrc()}
                            alt="Preview"
                            style={{ width: "100%", height: "auto", objectFit: "cover", borderRadius: "5px", marginTop: "5px" }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TextImage;
